import React from 'react';

const PrivacyPolicy = () => (
  <div className="Terms PrivacyPolicy">
    <h1>Marbleverse Privacy Policy</h1>
    <p>Valid from 3 May 2023</p>
    <p>
      Welcome to the Marbleverse. We provide an augmented reality Metaverse designed to enable you
      to interact in a common space that seamlessly merges with the real world. For this, we need
      some information about you. The protection of your privacy is very important to us. Your data
      will only be used by us if we have a legal basis for this use. This Privacy Policy helps you
      understand what information we collect, how we use it, and what choices you have when you use
      our app, use our websites, or participate in live events and promotions (the “Services”).
    </p>
    <p>
      Please note that in addition to the general information below, specific information about how
      certain our services use your information is provided at the end of this Privacy Policy in the
      “App Specific Information” section.
    </p>
    <p>
      It is important that you also read our End User License Agreement (EULA), which constitute the
      agreement between you and Marble AR, Inc., 440 N Barranca Ave #1141, Covina, CA 91723, USA
      ("Marble").
    </p>
    <ol>
      <li>Who decides how your data will be used?</li>
    </ol>
    <p>Marble is responsible for making decisions about how we use your personal information.</p>
    <p>If you have any questions or comments about this policy, you can:</p>
    <p>
      – email us at <a href="mailto:contact@marblear.com">contact@marblear.com</a>
    </p>
    <p>
      – contact our data protection officer at{' '}
      <a href="mailto:contact@marblear.com">contact@marblear.com</a>
    </p>
    <ol start="2">
      <li>The data we collect about you and how we use it</li>
    </ol>
    <p>
      This Privacy Policy applies to our use of information that may or may be used to identify you
      (“Personal Information”). It does not apply to data that cannot be used to identify you
      (“Anonymous Data”).
    </p>
    <p>
      We must collect and use certain personal Information in order to provide you with the services
      and fulfill the commitments we make to you in our Terms of Use:
    </p>
    <p>
      – When you register for our services, you voluntarily provide us with personal data by
      providing it to us, e.g. when you register an account. We collect and use this information to
      authenticate you when you register an account and use the Services to ensure that you are
      authorized and able to receive the Services and to provide you with the correct version of the
      Services. This information includes the username within the platform that you choose to use
      within our services and the internal account identifiers that we associate with your account.
    </p>
    <p>
      – We collect and use your device location information when you use our services (and, if you
      enable background location detection, while not actively using the services), to show and
      notify you about content in your direct surroundings. Our services include a location-based
      platform whose central feature is to enable a user experience that is linked to real-world
      locations, so we need to know where you are to operate that platform for you and display
      resources within the app (for example, a Marble which you or another user has dropped at that
      location). We determine your location by using a variety of technologies, including GPS, WiFi,
      and mobile/cell triangulation.
    </p>
    <p>
      – We also collect and use your actions within the platform and your usage progress and certain
      information about your mobile device (including device identifiers, device operating system,
      model, configuration, settings and information about any third-party applications or software
      installed on your device) to provide the services to you and to ensure that we provide a fair
      user experience for all users in accordance with our Terms of Use (including anti-fraud and
      anti-cheating measures and prohibited conduct prohibited by the Terms of Use).
    </p>
    <p>
      – We also use the above information to show sponsored locations located near you as part of
      your user experience within the platform.
    </p>
    <p>
      – We also use the above information to provide you with technical support and customer
      service.
    </p>
    <p>
      – You also give us personal data if you subscribe to our publications, register for a live
      event, take part in a competition, advertising event, sweepstake, raffle or survey or
      communicate with us via us. Depending on which of these services you use, this may include
      your name, address, telephone number, country of residence, date of birth (if necessary to
      verify suitability), and e-mail address. We use this information to provide these services to
      you and to provide you with related customer support.
    </p>

    <p>
      In addition, we have and base the use of your Personal Information as follows on legitimate
      interests:
    </p>

    <p>
      – Use your IP address, browser type, operating system, website you visited before accessing
      our services, pages of our services you visited or features you used and the time you spent
      visiting those pages or features, links in our services you click on, device and advertising
      identifiers and actions they take during use, your app settings and preferences, and your
      purchases within the app to understand who uses our services and how our services are used.
    </p>
    <p>
      – Use of your contact information, namely your e-mail address, to communicate with you and to
      provide you with technical support and customer care.
    </p>
    <p>
      – Use your internal account identifier to associate user content with your account (such as
      local point of interests) where you choose to provide this to Marble through the Services.
    </p>
    <p>
      – Use your email address and device information to provide you with updates and news about the
      services either within the platform or via email. You can unsubscribe at any time via your
      device or app settings or a link in the footer of an e-mail.
    </p>
    <p>
      – To provide social features within our platform that enable you to interact with other users,
      including storing your communications with those users, finding and being found by your
      friends, and sharing your user experiences and successes with your friends.
    </p>
    <p>– To provide new or additional features for our services.</p>
    <p>
      – To organize and conduct live events based on our platform or identified by our platform.
      Please note that if you participate in live events, your actions and achievements within the
      platform, your username within the platform and your avatar, and other components of your user
      profile will be visible to other event participants and the public (e.g. at the event
      location).
    </p>
    <p>
      – To take anti-fraud and anti-cheating measures and to ensure that you and other participants
      comply with our Terms of Use.
    </p>
    <p>– To make legal or regulatory notices</p>

    <p>We use your personal data for the following only with your consent:</p>
    <p>
      – If you choose to enable background location tracking on our services (which will notify you
      about content in your vicinity, so you can easily find interesting Marbles dropped by other
      users), we will collect your Personal Information while the app is in the background, i.e. if
      you have not opened the services on your device. This collection includes your device
      location. We solely use this background location tracking to notify you about relevant content
      near you and do not store this location data permanently. You can change your mind and disable
      background location tracking in your device or app settings at any time.
    </p>
    <p>
      – Send marketing materials via email or app notifications. You can unsubscribe from these
      emails and app messages in your device or app settings or in the footer of e-mails at any
      time.
    </p>
    <p>
      We also rely on your consent when we use cookies or similar technologies.
      {/* You can find more information on how we use cookies in our Cookie Policy. */} As far as
      cookies collect personal data, we process these only on the basis of your explicit consent, in
      anonymous form or under a pseudonym.
    </p>

    <p>
      <strong>
        Extra information regarding Marbleverse's use of Apple's TrueDepth API on iOS devices
      </strong>
    </p>
    <p>
      Marbleverse uses the device’s TrueDepth camera system only to temporarily switch the camera
      into “selfie mode”, which allows you to capture photos or videos with the front camera. Only
      these photos or videos will be stored, but never any face data. Face data will not leave the
      device, is never stored remotely, or given to third parties.
    </p>

    <p>
      <strong>
        Extra information regarding Marbleverse’s use of Google Authentication Services
      </strong>
    </p>
    <p>
      If you sign-in to the Marbleverse using Sign In with Google, Marbleverse will retrieve your
      e-mail address, your first and last names and your Google profile picture to associate them
      with your Marble account. They will automatically appear in Marble, so you don’t have to type
      them in manually. If you choose to link your Google account to an already existing Marbleverse
      account, only your email address is retrieved.
    </p>

    <ol start="3">
      <li>With whom we share data</li>
    </ol>
    <p>
      We do not share any Personal Information we have collected from you or about you with others
      except as described below:
    </p>
    <p>
      Data shared with our service providers. We have contracted third parties to work with us to
      administer and provide the services. They process your personal data on our behalf. These
      third parties have access to your Personal Information only for the purpose of providing the
      services we have commissioned, in accordance with this Privacy Policy, and we ensure that each
      third party is contractually bound not to disclose or use your Personal Information for any
      other purpose. The third party service providers we use will assist us with the following:
    </p>
    <p>
      – Run, operate and maintain our mobile games through third-party platforms and software tools;
    </p>
    <p>– Moderation and crash analytics;</p>
    <p>– Conduct email and mobile marketing campaigns;</p>
    <p>– Perform game and marketing analytics;</p>
    <p>
      – Manage live events, contests, sweepstakes and promotional events, including user
      registration, registration and attendance management, eligibility verification and pricing;
    </p>
    <p>– providing technical support and customer care;</p>
    <p>– Processing of payments for live event ticket sales or other purchases.</p>
    <p>
      Data shared with other users. If you use the Services, and in particular if you use our App,
      if you use social features of this Platform, or participate in live events, we will share
      certain Personal Information with other users. This Personal Information includes your user
      profile (such as your username), your actions within the Platform and usage progress, the
      location of resources within the Platform in the real world with which you interact while
      using the Platform, and your public messages within the App. Please read the app-specific
      information at the end of this Privacy Policy for more details.
    </p>
    <p>
      Data shared with third parties. We share anonymous data with third parties for industry and
      market analysis. If you have given us your explicit consent, we may share personal information
      with third party publishers for direct marketing purposes. We do not share Personal
      Information with other third parties for their direct marketing purposes.
    </p>
    <p>
      Data passed on in connection with business transactions. Data that we collect from our users,
      including personal data, is part of our business assets. If our company is acquired by a third
      party as a result of a transaction such as a merger, acquisition or asset deal, or if our
      assets are acquired by a third party in the event of our cessation of business or insolvency,
      all or part of our assets, including your Personal Data, will be transferred or transferred to
      a third party acquirer in connection with the transaction.
    </p>
    <ol start="4">
      <li>How your personal data is transmitted</li>
    </ol>
    <p>
      Marble operates its services worldwide. Your Personal Information will most likely be
      transferred and stored in a country outside your home country, including the United States,
      for the purposes described in this Privacy Policy. Data protection laws in these countries may
      not be the same as those in your home country.
    </p>
    <p>
      When we transfer your Personal Information from the EEA to other countries, including the
      United States, we ensure that your Personal Information is afforded a similar level of
      protection as within the EEA by ensuring that at least one of the following guarantees is
      implemented:
    </p>
    <p>
      – The country to which your Personal Data is transferred is a country which, in the opinion of
      the European Commission, provides an adequate level of protection for Personal Data, such as
      the EEA.
    </p>
    <p>
      – Where we work with service providers in the United States, they are part of the EU-U.S.
      Privacy Shields, under which they are required to provide the same level of protection for
      your Personal Information as they do within the EEA.
    </p>

    <ol start="5">
      <li>How we secure your personal data</li>
    </ol>
    <p>
      We have taken reasonable security measures to protect your Personal Information from
      accidental loss, unlawful use or access, adaptation or disclosure. We also restrict access to
      your personally identifiable information to employees, contractors and other third parties who
      need to know that information for business purposes. You will only process your Personal Data
      in accordance with our instructions and it will be subject to a duty of confidentiality. We
      have established procedures to deal with a suspected breach of Personal Data Protection and we
      will notify you and the appropriate regulatory authority of a breach if we are required to do
      so by law.
    </p>

    <ol start="6">
      <li>How long will we store your personal data?</li>
    </ol>
    <p>
      We will retain your Personal Information for as long as we need it to provide the Services to
      you and for the purposes set out in this Privacy Policy. This also applies to anyone with whom
      we share your data and who provides services on our behalf.
    </p>
    <p>
      When we no longer need your Personal Information and when we no longer need to store it to
      comply with our legal or regulatory obligations, we will delete your information from our
      systems or make it anonymous so that it can no longer be associated with you. When we delete
      Personal Information, we will take commercially reasonable and technically feasible steps to
      delete that Personal Information irretrievably or irretrievably.
    </p>

    <ol start="7">
      <li>Your rights and opportunities</li>
    </ol>
    <p>
      You have certain rights with regard to your personal data. To exercise these rights, please
      contact us at <a href="mailto:contact@marblear.com">contact@marblear.com</a>
    </p>
    <p>You can:</p>
    <p>– Request information about the personal data that we have about you.</p>
    <p>
      – Delete or correct your personal data. The easiest way to update your account information is
      through your in-app settings. You can also submit a customer support request to us at
      contact@marblear.com .
    </p>
    <p>
      – You object to the processing of your personal data. Some of the personal data we store is
      necessary for the provision of the services and for fulfilling the promises made to you in the
      Terms of Use.
    </p>
    <p>
      – Require us to cease using your Personal Information, including for marketing and promotional
      purposes (but remember that sometimes we need to use your Personal Information in order for
      you to use the Services).
    </p>
    <p>
      – Have your Personal Information transferred to another company (where technically possible).
    </p>
    <p>
      – Complain to a regulatory authority. We would be pleased if you would give us the opportunity
      to deal with your concerns directly, so we would prefer if you contact us first. However, if
      you are based in the EEA and believe that we have not complied with data protection laws, you
      can complain to your local regulatory authority.
    </p>
    <p>
      Under certain circumstances, the laws provide for exceptions to these rights. If you are
      unable to exercise any of these rights because of such an exception, we will explain why.
    </p>
    <p>
      We provide you with opportunities to collect, use and share Personal Information and will
      respect the choices you make. Please note that if you choose not to provide the Personal
      Information we request, you may not have access to all the features of the Services.
    </p>
    <p>
      Our goal is to provide the data within 30 days or to fully respond to your request within this
      time.
    </p>

    <ol start="8">
      <li>Changes to this privacy policy</li>
    </ol>
    <p>
      Personal information that we collect is covered by the Privacy Policy that was in effect when
      that information was collected. We may change this Privacy Policy from time to time. We will
      give you reasonable notice of any material changes.
    </p>

    <ol start="9">
      <li>Third parties</li>
    </ol>
    <p>
      By using our services, you have access to links to websites and services owned or operated by
      third parties (each a “Third Party Provider”). Information that you provide in or through a
      third party service is provided directly to the owner or operator of the service and is
      subject to the Privacy Policy of the owner or operator. We are not responsible for the
      content, privacy, security measures and policies of any third party services. To protect your
      privacy, we encourage you to carefully review the privacy policies of all third parties you
      access.
    </p>
    <p>
      Data shared with other users. When you perform certain actions in the Marbleverse app, your
      username (or the username of your authorized child) will be publicly shared across the
      platform, including with other users, in connection with the arena location in the “Marble
      Spot”.
    </p>
  </div>
);

export default PrivacyPolicy;
